<div class="checkout-custom-upsell">
  <form [formGroup]="formGroup">
    <div class="content">
      @if (updateRequired) {
        <pdq-banner
          variant="attention"
          [closable]="false"
          bannerTitle="Update Needed"
          content="Before you can save this extension, you'll need to update the PDQ app on Shopify."
          button="Update App In Shopify"
          (onButtonClick)="onUpdateAppPermissionsClick()">
        </pdq-banner>
      }

      <pdq-card class="card" variant="popover" color="white">
        <div class="card-title"><pdq-heading element="h5">Token for Shopify</pdq-heading></div>
        <pdq-divider></pdq-divider>
        <div class="card-container">
          <pdq-link
            label="View detailed installation instructions"
            [external]="true"
            [url]="'https://support.prettydamnquick.com/en/articles/8560867-adding-multiple-pdq-components'"></pdq-link>
          <pdq-input
            [disabled]="true"
            formControlName="usageToken"
            label="Copy & paste this into Shopify's Components by 'Custom upsell token'"
            [clipboard]="true"></pdq-input>
          <pdq-extension-preview-token [usageTokenControl]="formGroup.get('usageToken')"></pdq-extension-preview-token>
        </div>
      </pdq-card>
      <pdq-card variant="popover" color="white">
        <div class="card-title"><pdq-heading element="h5">Settings</pdq-heading></div>
        <pdq-divider></pdq-divider>
        <div class="card-container">
          <pdq-input formControlName="internalName" label="Internal name" placeholder="Digital upsell"></pdq-input>
          <pdq-input
            formControlName="title"
            label="Title"
            placeholder="Priority processing"
            [invalid]="formGroup.controls.title.errors?.required"
            [invalidMsg]="formGroup.controls.title.errors?.required ? 'Title is required' : ''"></pdq-input>
          <pdq-textarea
            [control]="formGroup.controls?.description"
            label="Description text"
            placeholder="Get special service for your order"></pdq-textarea>

          <div style="font-size: 14px">
            <pdq-input-file
              [formControl]="formGroup.controls?.productImageBase64"
              (onPreviewChange)="onProductImageChange($event)"
              [label]="'Upload product image'"
              submitButtonLabel="Upload image"
              tooltip=".png, maximum size 500KB"
              [supportedFormat]="['image/png']"
              [maxKBSize]="500"
              (onClearFile)="onClearImage()"></pdq-input-file>
          </div>
          <div class="upsell-section gap-16">
            <pdq-radio-group formControlName="placementMode" label="Component placement">
              <pdq-radio-button
                [value]="PlacementMode.STANDALONE"
                text="Standalone"
                [checked]="formGroup.value['placementMode'] === PlacementMode.STANDALONE"></pdq-radio-button>
              <pdq-radio-button
                [value]="PlacementMode.LINKED"
                [checked]="formGroup.value['placementMode'] === PlacementMode.LINKED">
                <ng-template #customContent>
                  <div class="linked-mode-radio-content">
                    <p class="linked-mode-radio-content__title caption-medium-caps">Inside shipping method</p>
                    <p class="linked-mode-radio-content__description body-12-regular">
                      Go to your
                      <pdq-link
                        [clickable]="true"
                        [external]="true"
                        size="small"
                        label="Online Store Editor"
                        [url]="shopService.ecommerceEditorUrl()"></pdq-link>
                      to define which shipping method this extension shows up for
                    </p>
                  </div>
                </ng-template>
              </pdq-radio-button>
            </pdq-radio-group>
          </div>
          <div class="upsell-section gap-16">
            <pdq-radio-group
              formControlName="upsellRateType"
              [disabled]="!!loadedExtension"
              label="Choose type"
              labelSize="small"
              tooltip="This setting cannot be edited once the digital item has been created.">
              @for (upsellRateType of upsellRateTypeConfig; track upsellRateType) {
                <pdq-radio-button
                  [value]="upsellRateType.value"
                  [checked]="upsellRateType.checked(formGroup.value['upsellRateType']) || upsellRateType.defaultChecked"
                  [disabled]="upsellRateType.disabled(!!loadedExtension)"
                  [text]="upsellRateType.text">
                </pdq-radio-button>
              }
            </pdq-radio-group>
          </div>

          <div class="upsell-section gap-16">
            @if (formGroup.value['upsellRateType'] === 'fixed') {
              <div class="flex-column" style="width: 280px">
                <pdq-input-arrows
                  widthSize="fullwidth"
                  precision="2"
                  min="0"
                  formControlName="upsellRate"
                  label="Upsell rate"
                  [symbol]="(shop$ | async)?.metadata?.currency | currencySign"
                  placeholder="6.00"></pdq-input-arrows>

                <pdq-dropdown
                  (onChange)="onApplyDiscountToChange($event)"
                  [options]="applyDiscountToOptions"
                  [dirty]="false"
                  title="Apply Discount To"
                  [defaultOption]="
                    formGroup.get('applyDiscountTo')?.value | primitiveDropdownOptionPipe: camelCaseToTitleCase
                  "></pdq-dropdown>
                <pdq-input-arrows
                  widthSize="fullwidth"
                  symbol="%"
                  min="0"
                  formControlName="discount"
                  [singleLineLabel]="true"
                  [label]="
                    formGroup.get('applyDiscountTo')?.value === 'orderSubtotal'
                      ? 'Discount To Order Excluding Digital Upsell Price'
                      : 'Discount'
                  "
                  placeholder="20"
                  max="100"></pdq-input-arrows>
              </div>
            } @else {
              <div class="flex-column">
                <pdq-input-arrows
                  widthSize="xxlarge"
                  precision="1"
                  max="100"
                  min="0"
                  formControlName="upsellRatePercentage"
                  label="Upsell cart percentage"
                  [symbol]="'%'"></pdq-input-arrows>

                <pdq-input-arrows
                  widthSize="xxlarge"
                  precision="2"
                  min="0"
                  placeholder="0"
                  formControlName="minCartValueThreshold"
                  label="Minimum cart value"
                  [required]="false"
                  [symbol]="(shop$ | async)?.metadata?.currency | currencySign"></pdq-input-arrows>
                <pdq-input-arrows
                  widthSize="xxlarge"
                  precision="2"
                  min="0"
                  formControlName="maxCartValueThreshold"
                  placeholder="No limit"
                  label="Maximum cart value"
                  [required]="false"
                  [symbol]="(shop$ | async)?.metadata?.currency | currencySign"></pdq-input-arrows>
              </div>
            }
          </div>

          <pdq-checkbox formControlName="tooltipEnabled" label="Enable tooltip for extra details"> </pdq-checkbox>
          @if (formGroup.value.tooltipEnabled) {
            <pdq-input label="Tooltip title" formControlName="tooltipTitle"></pdq-input>
            <pdq-textarea
              maxLength="5000"
              label="Tooltip body"
              [control]="formGroup.controls.tooltipText"></pdq-textarea>
          }

          @if ((shop$ | async)?.metadata?.extensions?.customUpsell?.improvedDeliveryAssurance) {
            <div class="body-14-regular">
              <pdq-input-file
                [formControl]="formGroup.controls['infoPopupImage']"
                (onPreviewChange)="onInfoPopupImageChange($event)"
                [label]="'Upload info pop-up image'"
                submitButtonLabel="Upload image"
                tooltip=".png/.jpg, maximum size 1MB"
                [supportedFormat]="['image/png', 'image/jpeg']"
                [maxKBSize]="1000"
                (onClearFile)="onClearInfoPopupImage()"></pdq-input-file>
            </div>
          }
        </div>
      </pdq-card>

      @if ((shop$ | async)?.metadata?.extensions?.customUpsell?.improvedDeliveryAssurance) {
        <pdq-card variant="popover" color="white">
          <div formGroupName="trustSignals">
            <div class="card-title">
              <div class="card-title-text">
                <pdq-heading element="h5">Trust signals</pdq-heading>
                <span class="card-subtitle">Show reassurance badges under the upsell.</span>
              </div>
              <pdq-slide-toggle
                formControlName="enabled"
                [defaultValue]="formGroup.get('trustSignals.enabled')"></pdq-slide-toggle>
            </div>
            <pdq-divider></pdq-divider>
            @if (formGroup.get('trustSignals.enabled')?.value) {
              <div class="card-container flex-column gap-16">
                <pdq-radio-group formControlName="layout" label="Layout">
                  @for (opt of trustSignalsLayoutOptions; track opt.value) {
                    <pdq-radio-button
                      [value]="opt.value"
                      [text]="opt.text"
                      [checked]="formGroup.get('trustSignals.layout')?.value === opt.value"></pdq-radio-button>
                  }
                </pdq-radio-group>
                <span class="card-subtitle">Badge style — applies to all signals</span>
                <div class="trust-signal-style-row">
                  <pdq-dropdown
                    formControlName="backgroundColor"
                    title="Background"
                    [options]="trustSignalsBackgroundOptions"></pdq-dropdown>
                  <pdq-dropdown
                    formControlName="border"
                    title="Border"
                    [options]="trustSignalsBorderOptions"></pdq-dropdown>
                  <pdq-dropdown
                    formControlName="cornerRadius"
                    title="Corners"
                    [options]="trustSignalsCornerOptions"></pdq-dropdown>
                </div>
                <div class="flex-column gap-16" formArrayName="items">
                  @for (item of trustSignalItems.controls; track item; let i = $index) {
                    <div class="trust-signal-item flex-column gap-8" [formGroupName]="i">
                      <div class="trust-signal-item__header">
                        <span class="card-subtitle">Signal {{ i + 1 }}</span>
                        <pdq-button
                          icon="trash"
                          variant="ghost"
                          iconColor="var(--error-default)"
                          size="small"
                          (onClick)="removeTrustSignal(i)"></pdq-button>
                      </div>
                      <pdq-iconpicker
                        [presetIconUrls]="trustSignalIconKeys"
                        [presetIconLabels]="trustSignalIconLabels"
                        [presetsAsIcons]="true"
                        [supportsCustomUpload]="false"
                        [iconUrl]="item.get('icon')?.value"
                        (iconUrlChange)="onTrustSignalIconChange(i, $event)"></pdq-iconpicker>
                      <rich-text-editor
                        [label]="'Text'"
                        [control]="item.get('richText')"
                        [enableLinkInsert]="true"></rich-text-editor>
                    </div>
                  }
                </div>
                <pdq-button
                  label="+ Add trust signal"
                  variant="primary"
                  size="medium"
                  [fitContent]="true"
                  (onClick)="addTrustSignal()"></pdq-button>
              </div>
            }
          </div>
        </pdq-card>
      }

      <pdq-card variant="popover" color="white">
        <div formGroupName="autoAdd">
          <div class="card-title">
            <div class="card-title-text">
              <pdq-heading element="h5">Auto-add</pdq-heading>
              <span class="card-subtitle">Add upsells to the cart before the shopper opts in.</span>
            </div>
            <pdq-slide-toggle
              formControlName="enabled"
              [defaultValue]="formGroup.get('autoAdd.enabled')"></pdq-slide-toggle>
          </div>
          <pdq-divider></pdq-divider>
          @if (formGroup.value.autoAdd?.enabled) {
            <div class="card-container">
              <pdq-radio-group formControlName="scope" label="Apply to">
                <pdq-radio-button
                  [value]="'freeOnly'"
                  [checked]="formGroup.value.autoAdd?.scope === 'freeOnly'"
                  text="Free upsells only"
                  subText="Only when the item is free"></pdq-radio-button>
                <pdq-radio-button
                  [value]="'all'"
                  [checked]="formGroup.value.autoAdd?.scope === 'all'"
                  text="All upsells"
                  subText="Whether the item is free or paid"></pdq-radio-button>
              </pdq-radio-group>

              <pdq-radio-group formControlName="mode" label="Behavior">
                <pdq-radio-button
                  [value]="'once'"
                  [checked]="formGroup.value.autoAdd?.mode === 'once'"
                  text="Optional"
                  subText="Added and pre-checked. Shopper can uncheck it."></pdq-radio-button>
                <pdq-radio-button
                  [value]="'persistent'"
                  [checked]="formGroup.value.autoAdd?.mode === 'persistent'"
                  text="Always included"
                  subText="Shopper can't remove it from the cart, as long as display conditions are met">
                  <ng-template #afterContent>
                    @if (formGroup.value.autoAdd?.mode === 'persistent') {
                      <div class="autoadd-nested">
                        <pdq-divider orientation="vertical"></pdq-divider>
                        <pdq-radio-group
                          [formControl]="$any(formGroup.get('autoAdd.persistentCheckbox'))"
                          label="Checkbox appearance">
                          <pdq-radio-button
                            [value]="'disabled'"
                            [checked]="formGroup.value.autoAdd?.persistentCheckbox === 'disabled'"
                            text="Show as locked"
                            subText="Visible and checked, can't be unchecked"></pdq-radio-button>
                          <pdq-radio-button
                            [value]="'hidden'"
                            [checked]="formGroup.value.autoAdd?.persistentCheckbox === 'hidden'"
                            text="Hide"
                            subText="Not shown to the shopper"></pdq-radio-button>
                        </pdq-radio-group>
                      </div>
                    }
                  </ng-template>
                </pdq-radio-button>
              </pdq-radio-group>
            </div>
          }
        </div>
      </pdq-card>

      @if (formGroup.value['upsellRateType'] !== 'fixed') {
        <pdq-card variant="popover" color="white">
          <div formGroupName="customProductConfiguration">
            <div class="card-title">
              <pdq-heading element="h5">Custom product configuration</pdq-heading>
              <pdq-slide-toggle
                formControlName="enabled"
                [tooltip]="
                  !!loadedExtension ? 'This setting cannot be edited once the digital item has been created.' : ''
                "
                [disabled]="!!loadedExtension"
                [defaultValue]="formGroup.get('customProductConfiguration.enabled')">
              </pdq-slide-toggle>
            </div>
            <pdq-divider></pdq-divider>
            <div class="card-container">
              <span>Use this mode to define how the products and variants are created in Shopify.</span>
              <span class="sub-title"
                >By default, PDQ will create one product for this digital upsell. "Percent" mode will create 100
                variants spread equaliy around your typical AOV.</span
              >
              <pdq-input-arrows
                widthSize="xxlarge"
                min="1"
                max="5"
                placeholder="1"
                formControlName="numOfProducts"
                label="Number of products"
                [disabled]="
                  !formGroup?.controls?.customProductConfiguration?.value?.enabled ||
                  (!!loadedExtension && formGroup?.controls?.customProductConfiguration?.value?.numOfProducts > 0)
                "
                tooltip="Limited to 5 products, cannot be edited once the digital product has been created."
                [required]="true"></pdq-input-arrows>
              <pdq-input-arrows
                widthSize="xxlarge"
                precision="2"
                min="0.01"
                max="100"
                formControlName="variantsPrecision"
                placeholder="0.01"
                [disabled]="
                  !formGroup?.controls?.customProductConfiguration?.value?.enabled ||
                  (!!loadedExtension && formGroup?.controls?.customProductConfiguration?.value?.variantsPrecision > 0)
                "
                label="Precision between variants"
                [required]="true"></pdq-input-arrows>
            </div>
          </div>
        </pdq-card>
      }

      <pdq-card variant="popover" color="white">
        <div class="card-title"><pdq-heading element="h5">Custom order tags</pdq-heading></div>
        <pdq-divider></pdq-divider>
        <div class="card-container">
          <span>Add tags to any order that selects this upsell</span>
          <div class="flex-column">
            <pdq-checkbox
              formControlName="prioritizeOrder"
              label="Add another Priority Processing tag and pin order to top of PDQ screen"></pdq-checkbox>
            @if (formGroup.value.prioritizeOrder) {
              <pdq-tag variant="quaternary" mode="rect"> Priority Order </pdq-tag>
            }
            <pdq-input placeholder="Enter tag" formControlName="customTag"></pdq-input>
            @if (formGroup.value.customTag) {
              <pdq-tag variant="quaternary" mode="rect">{{ formGroup.value.customTag }}</pdq-tag>
            }
          </div>
        </div>
      </pdq-card>

      <pdq-card variant="popover" color="white">
        <div class="card-title">
          <pdq-heading element="h5">Display conditions</pdq-heading>
        </div>
        <pdq-divider></pdq-divider>
        <div class="card-container">
          <p>This Digital Upsell Will Only Appear If:</p>
          <pdq-rules-dynamic-form
            [formGroup]="formGroup"
            [formArrayFieldName]="formArrayFieldName"
            [rules]="restrictionsRules | addCurrencyOption: shopCurrencySymbol"
            [entities]="entities"
            [shopCurrencySymbol]="shopCurrencySymbol"
            [versionWithOrOperator]="true"></pdq-rules-dynamic-form>

          <div class="upsell-section gap-16">
            <pdq-radio-group
              formControlName="keepInCart"
              label="When cart no longer meets conditions"
              tooltip="if a customer's cart no longer meets the upsell conditions, choose whether to keep or automatically remove the upsell">
              <pdq-radio-button
                [value]="true"
                [checked]="formGroup.value.keepInCart === true"
                text="Keep In Cart"></pdq-radio-button>
              <pdq-radio-button
                [value]="false"
                [checked]="formGroup.value.keepInCart === false"
                text="Remove From Cart"></pdq-radio-button>
            </pdq-radio-group>
          </div>
        </div>
      </pdq-card>
    </div>
  </form>
</div>
